import Add from "@mui/icons-material/Add";
import Delete from "@mui/icons-material/Delete";
import {
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CopyableText } from "components/CopyableText";
import { Control, FieldValues, Path, useFieldArray } from "react-hook-form";
import { EditablePasswordField } from "./EditablePasswordField";

interface SecurityQuestionsFieldProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  disabled?: boolean;
  canViewCredentials?: boolean;
}

export function SecurityQuestionsField<T extends FieldValues>({
  name,
  control,
  disabled,
  canViewCredentials,
}: SecurityQuestionsFieldProps<T>): JSX.Element {
  const { fields, append, remove } = useFieldArray({
    control,
    name: name as never,
  });

  return (
    <Box sx={{ mt: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle2">Security Questions</Typography>
        <Button
          size="small"
          startIcon={<Add />}
          onClick={() => append({ question: "", answer: "" } as never)}
          disabled={disabled}
        >
          Add Question
        </Button>
      </Stack>

      {fields.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No security questions configured
        </Typography>
      )}

      <Stack spacing={2}>
        {fields.map((field, index) => (
          <Box
            key={field.id}
            sx={{
              p: 2,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <Stack direction="row" alignItems="flex-start" spacing={1}>
              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Question"
                  disabled={disabled}
                  autoComplete="off"
                  sx={{ mb: 1 }}
                  {...control.register(`${name}.${index}.question` as Path<T>)}
                />
                <EditablePasswordField
                  name={`${name}.${index}.answer` as Path<T>}
                  control={control}
                  label={
                    <>
                      Answer
                      {canViewCredentials && (
                        <CopyableText hideText>
                          {(field as { answer?: string }).answer}
                        </CopyableText>
                      )}
                    </>
                  }
                  disabled={disabled}
                  canViewCredentials={canViewCredentials}
                  mode="add"
                />
              </Box>
              <IconButton
                size="small"
                onClick={() => remove(index)}
                disabled={disabled}
                color="error"
                sx={{ mt: 1 }}
              >
                <Delete />
              </IconButton>
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
